<template>
  <div class="container my-3">
    <h3 class="text-center">Quality of Service Indicators</h3>
    <div class="row">
      <div class="chart-container col-4 text-center">
        <Line v-if="isLoadedByMeasType.sinr" id="sinrChart" :data="dataByMeasType.sinr"
          :options="optionsByMeasType.sinr" />
        <p v-else>
          No data available for QoS SINR Chart
        </p>
      </div>
      <div class="chart-container col-4 text-center">
        <Line v-if="isLoadedByMeasType.rsrp" id="rsrpChart" :data="dataByMeasType.rsrp"
          :options="optionsByMeasType.rsrp" />
        <p v-else>
          No data available for QoS RSRP Chart
        </p>
      </div>
      <div class="chart-container col-4 text-center">
        <Line v-if="isLoadedByMeasType.rsrq" id="rsrqChart" :data="dataByMeasType.rsrq"
          :options="optionsByMeasType.rsrq" />
        <p v-else>
          No data available for QoS RSRQ Chart
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  Legend,
  CategoryScale,
  Colors,
  Title,
} from 'chart.js';
import { Line } from 'vue-chartjs';
import { apiFetch } from '@/utils/api';
import { isProd, isMockData, ApiEndpoint } from '@/utils/config';

// Define component metadata if needed (Optional in Vue 3, defaults to filename)
defineOptions({ name: 'QosChart' });

// Register ChartJS modules
ChartJS.register(LineController, LineElement, PointElement, LinearScale, Title, Tooltip, CategoryScale);

// Reactive State Variables
const isLoadedByMeasType = ref({});
const dataByMeasType = ref({});
const optionsByMeasType = ref({});
const qosLoaded = ref(false);

const loadQos = async (filters = {}) => {
  let qos = [];
  const queryParams = [];
  for (const k in filters) {
    queryParams.push(`${k}=${filters[k]}`);
  }
  if (queryParams.length === 0) {
    queryParams.push(`limit=100`);
  }
  const query = queryParams.length > 0 ? ('?' + queryParams.join('&')) : '';
  
  try {
    ({ items: qos } = await apiFetch(`${ApiEndpoint.COVERAGE}/qos${query}`));
    console.debug(`QoS loaded ${qos.length} data entries`);
  } catch (err) {
    if (!isProd && isMockData) {
      try {
        console.warn(`Dev Mode: Falling back to local QoS mocks (${err.message})`);
        const { default: mockData } = await import('@/mocks/test_qos.json');
        qos = mockData;
      } catch {
      }
    }
  }

  const measTypes = ['sinr', 'rsrp', 'rsrq'];
  let measTimes = [];
  
  for (const data of qos) {
    if (!Object.keys(data).some(m => measTypes.includes(m))) continue;
    if (typeof data.measurementTime === 'string') {
      measTimes.push(
        data.measurementTime.replace('T', ' ').replace('Z', '')
      );
    }
  }
  measTimes = measTimes.sort((a, b) => new Date(a) - new Date(b));

  for (const data of qos) {
    if (!Object.keys(data).some(m => measTypes.includes(m))) continue;
    if (filters.beams) {
      const beamFilter = Array.isArray(filters.beams) 
        ? filters.beams
        : [filters.beams];
      if (!beamFilter.some(f => data.beamId.startsWith(f))) continue;
    }
    
    for (const measType of measTypes) {
      if (Array.isArray(filters.measTimes) && !filters.measTypes.includes(measType)) {
        isLoadedByMeasType.value[measType] = false;
        continue;
      }
      
      if (!(measType in dataByMeasType.value)) {
        dataByMeasType.value[measType] = { datasets: [], labels: measTimes };
        optionsByMeasType.value[measType] = {
          maintainAspectRatio: false,
          scales: {
            x: {
              title: { display: false, text: 'Time' },
              ticks: { maxRotation: 90, minRotation: 90 },
            },
            y: {
              title: {
                display: true,
                text: ['sinr', 'rsrq'].includes(measType) ? 'dB' : 'dBm',
              }
            }
          },
          plugins: {
            title: { display: true, text: measType.toUpperCase() },
            legend: { display: false },
          }
        };
      }
      
      const { datasets } = dataByMeasType.value[measType];
      let dsi = datasets.findIndex(e => e.label === data.imsi);
      if (dsi === -1) {
        datasets.push({ type: 'line', label: data.imsi, data: [] });
        dsi = datasets.length - 1; // Correct index target for pushed item
      }
      
      let mTime = data.measurementTime.replace('T', ' ').replace('Z', '');
      datasets[dsi].data.push({ [mTime]: data[measType] });
    }
  }

  for (const measType of measTypes) {
    if (!dataByMeasType.value[measType]) return;
    let datasets = dataByMeasType.value[measType].datasets;
    
    for (const imsiData of datasets) {
      let tempData = [];
      for (const measLabel of measTimes) {
        const result = imsiData.data.find((obj) => Object.keys(obj).includes(measLabel));
        if (result) {
          tempData.push(result[measLabel]);
        } else {
          tempData.push(null);
        }
      }
      imsiData.data = tempData;
    }
    isLoadedByMeasType.value[measType] = true;
  }
  
  qosLoaded.value = true;
};

// Lifecycle Hook
onMounted(async () => {
  await loadQos();
});
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 50vh;
}
</style>
